import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from 'avn/modules/services/constants';
import { StoreService } from 'avn/modules/services/store.service';
import { Observable, catchError, throwError } from 'rxjs';

export interface IProfileApiPayload {
  [key: string]: unknown;
}

export interface IProfileResponse {
  detail?: string;
  personal?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_no?: string;
    gender?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface IMessageResponse {
  message?: string;
  [key: string]: unknown;
}

export interface IStatusResponse {
  status?: number;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileNewService {
  private readonly errorHandler: ErrorHandler;

  public constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly store: StoreService,
    private readonly injector: Injector
  ) {
    this.errorHandler = this.injector.get(ErrorHandler);
  }

  public redirectToLogin(): void {
    void this.router.navigate(['/login'], { queryParams: { redirect: this.router.url } });
  }

  public getProfile(): Observable<IProfileResponse> {
    const endPoint = `/profile/?v=${this.store.getRandomNumber(true)}`;
    return this.http.get<IProfileResponse>(Constants.BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public fetchCustomerBookings(): Observable<unknown> {
    const endPoint = `/customer-bookings/?v=${this.store.getRandomNumber(true)}`;
    return this.http.get(Constants.BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public editProfile(data: IProfileApiPayload): Observable<IMessageResponse> {
    const endPoint = `/edit-profile/?v=${this.store.getRandomNumber(true)}`;
    return this.http
      .post<IMessageResponse>(Constants.BASE_URL + endPoint, data)
      .pipe(catchError(this.handleError.bind(this)));
  }

  public cancelBooking(id: number): Observable<unknown> {
    const endPoint = '/cancel-appointment/' + String(id) + '/';
    return this.http.get(Constants.BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public appointmentRating(data: IProfileApiPayload): Observable<unknown> {
    const endPoint = '/add-appointment-review/';
    return this.http.post(Constants.BASE_URL + endPoint, data).pipe(catchError(this.handleError.bind(this)));
  }

  public updatePassword(data: IProfileApiPayload): Observable<IStatusResponse> {
    const endPoint = '/change-password/';
    return this.http
      .post<IStatusResponse>(Constants.BASE_URL + endPoint, data)
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getWellbeingJourney(): Observable<unknown> {
    const endPoint = `wellbeing/personal-detail/?v=${this.store.getRandomNumber(true)}`;
    return this.http.get(Constants.BASE_URL_withoutAPI + endPoint);
  }

  public getMyList(): Observable<unknown> {
    const endPoint = `/get-lists/?v=${this.store.getRandomNumber(true)}`;
    return this.http.get(Constants.BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public updateMyList(uuid: string, list: IProfileApiPayload): Observable<unknown> {
    const endPoint = `/update-list/${uuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.http.post(Constants.BASE_URL + endPoint, list).pipe(catchError(this.handleError.bind(this)));
  }

  public getMyListDetail(uuid: string): Observable<unknown> {
    const endPoint = `/get-list-detail/${uuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.http.get(Constants.BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public createMyList(listData: IProfileApiPayload): Observable<unknown> {
    const endPoint = '/create-list/';
    return this.http.post(Constants.BASE_URL + endPoint, listData).pipe(catchError(this.handleError.bind(this)));
  }

  public addToList(uuid: string, params: IProfileApiPayload): Observable<unknown> {
    const endPoint = `/add-to-list/${uuid}/`;
    return this.http.post(Constants.BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public deleteMyList(listId: number): Observable<unknown> {
    const endPoint = `/delete-list/${String(listId)}/?v=${this.store.getRandomNumber(true)}`;
    return this.http.get(Constants.BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public deleteMyListItem(uuid: string): Observable<unknown> {
    const endPoint = `/remove-from-list/${uuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.http.get(Constants.BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getBlogsPage(): Observable<unknown> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .get(Constants.BLOG_POST_API_URL + '?per_page=3', { headers: headers })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getCategoryById(id: number): Observable<unknown> {
    const endPoint = '/' + String(id);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .get(Constants.BLOG_CATEGORIES_API_URL + endPoint, { headers: headers })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public updateNotification(params: IProfileApiPayload): Observable<IMessageResponse> {
    const endPoint = '/update-notification-preferences/';
    return this.http
      .post<IMessageResponse>(Constants.BASE_URL + endPoint, params)
      .pipe(catchError(this.handleError.bind(this)));
  }

  private handleError(error: unknown): Observable<never> {
    // Call the global error handler
    this.errorHandler.handleError(error);

    // Re-throw the error so it can be caught by the component's subscribe error handler
    return throwError(() => error);
  }
}
